/* eslint-disable no-unused-vars */
import "../assets/styles/promo.css";
import React, { useState, useEffect, useMemo, useRef } from "react";
import LoadingSpinner from "../components/shared/LoadingSpinner";
import {
  useResizeObserverErrorSuppression,
  useResponsiveDrawerWidth,
  formatCategories,
  parseCategories,
  parseQualifications,
  parseServices,
  parseGalleryImages,
  calculateAverageRating,
  getMonthName,
  governorates,
  getInitials,
  avatarColorFor,
  exportToExcel,
  openPrintWindow,
} from "../hooks";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  notification,
  Select,
  Avatar,
  Card,
  Row,
  Dropdown,
  Col,
  Drawer,
  Typography,
  Tooltip,
  Image,
  DatePicker,
  Tabs,
  Timeline,
  Spin,
  Tag,
  Switch,
} from "antd";

import { supabase } from "../supabaseClient";
import { notifyPartnerRequestsChanged } from "../hooks/fetch";
import { getDefaultPartnerPercent } from "../utils/partnerPercent";
import { fetchAvailableSuggestions } from "../utils/referralCodeSuggestions";
import VerifiedBadge from "../components/shared/VerifiedBadge";
import { useHistory, useLocation } from "react-router-dom";
import {
  TagOutlined,
  MailOutlined,
  CheckOutlined,
  PhoneOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  SafetyCertificateOutlined,
  GlobalOutlined,
  LinkedinOutlined,
  InstagramOutlined,
  TikTokOutlined,
  ToolOutlined,
  EditOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  FilePdfOutlined,
  SearchOutlined,
  FilterOutlined,
  EyeOutlined,
  UserAddOutlined,
  TeamOutlined,
  UserOutlined,
  UserSwitchOutlined,
  NotificationOutlined,
} from "@ant-design/icons";

// ─── Helpers ───────────────────────────────────────────────────────────────
function partnerIsAdmin(pn) {
  if (!pn && pn !== 0) return false;
  if (Array.isArray(pn)) return pn.map(String).some(s => s.trim().toLowerCase() === "admin");
  if (typeof pn === "string") {
    const parts = pn.split(/[,;|]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
    return parts.includes("admin");
  }
  return String(pn).trim().toLowerCase() === "admin";
}

/**
 * Normalise partner_name (string / JSON / array) en une liste d'IDs.
 * Même logique que Home.js pour que les vues filtrées correspondent aux KPI.
 */
function normalizePartnerIds(partner_name) {
  if (!partner_name) return [];
  if (Array.isArray(partner_name)) return partner_name.map(String).map(s => s.trim()).filter(Boolean);
  if (typeof partner_name === "string") {
    const t = partner_name.trim();
    try {
      const parsed = JSON.parse(t);
      if (Array.isArray(parsed)) return parsed.map(String).map(s => s.trim()).filter(Boolean);
      if (parsed) return [String(parsed).trim()];
    } catch (e) {
      return t.split(/[,;|]+/).map(s => s.trim()).filter(Boolean);
    }
  }
  return [String(partner_name).trim()];
}

/**
 * Calcule la date de fin en ajoutant exactement N mois à la date de début.
 * Ex: 16/05/2025 + 1 mois → 16/06/2025
 */
function addMonths(date, n) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + n);
  return d;
}

/**
 * Calcule la date de fin en ajoutant exactement N années à la date de début.
 * Ex: 16/05/2025 + 1 an → 16/05/2026
 */
function addYears(date, n) {
  const d = new Date(date);
  d.setFullYear(d.getFullYear() + n);
  return d;
}

// ─── Composant principal ────────────────────────────────────────────────────
function PartnerClients() {
  const { Title, Text } = Typography;

  useResizeObserverErrorSuppression();
  const drawerWidth = useResponsiveDrawerWidth();

  const [form] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [clientss, setPartners] = useState([]);
  const [editingSponsorId, setEditingSponsorId] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [echeanceModal, setEcheanceModal] = useState({ open: false, record: null });
  const [filterStatus, setFilterStatus] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [governorateFilter, setGovernorateFilter] = useState("all");
  const [partnerFilter, setPartnerFilter] = useState("all");
  const [adminMap, setAdminMap] = useState({});
  const [promotingId, setPromotingId] = useState(null);
  const [promoteModalOpen, setPromoteModalOpen] = useState(false);
  const [selectedProForPromotion, setSelectedProForPromotion] = useState(null);
  const [promoteCode, setPromoteCode] = useState("");
  const [promoteCodeCheck, setPromoteCodeCheck] = useState({ status: "idle", message: "" });
  const [promoteCodeSuggestions, setPromoteCodeSuggestions] = useState({ loading: false, items: [] });
  const [paymentPreview, setPaymentPreview] = useState({ open: false, loading: false, basePrice: 0, finalPrice: 0, discount: 0, promoId: null, years: 1, total: 0, totalOriginal: 0, profession: "" });
  const [notificationModal, setNotificationModal] = useState({ open: false, record: null });
  const [notificationText, setNotificationText] = useState("");
  const [notificationSending, setNotificationSending] = useState(false);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [notificationTab, setNotificationTab] = useState("send");
  const notificationHistoryCache = useRef(new Map());

  // ─── Drawer state ──────────────────────────────────────────────────────────
  const [open, setOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  const [galleryPage, setGalleryPage] = useState(1);
  const galleryPageSize = 6;
  const [paymentsPage, setPaymentsPage] = useState(1);
  const paymentsPageSize = 5;

  // ─── État formulaire paiement ──────────────────────────────────────────────
  // Frais d'inscription : 1 date de début + nombre d'ANNÉES
  const [isAddingEcheance, setIsAddingEcheance] = useState(false);
  const [echeanceForm, setEcheanceForm] = useState({
    activated_at: null,      // Dayjs object (AntD DatePicker)
    duration_years: 1,       // nombre d'années choisi par l'admin
  });

  // Calcul automatique de la date de fin (preview)
  const computedEndDate = useMemo(() => {
    if (!echeanceForm.activated_at) return null;
    const start = echeanceForm.activated_at.toDate
      ? echeanceForm.activated_at.toDate()
      : new Date(echeanceForm.activated_at);
    return addYears(start, echeanceForm.duration_years);
  }, [echeanceForm.activated_at, echeanceForm.duration_years]);

  // ─── Responsive ────────────────────────────────────────────────────────────
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowWidth < 600;
  const isTablet = windowWidth >= 600 && windowWidth < 1024;
  const isSmallDesktop = windowWidth >= 1024 && windowWidth < 1366;
  const isMediumDesktop = windowWidth >= 1366 && windowWidth < 1600;
  const isLargeDesktop = windowWidth >= 1600;
  const getPageSize = () => 10;
  const pageSize = getPageSize();
  const history = useHistory();
  const location = useLocation();

  // ─── Périmètre de gestion (depuis Home : ?tab=admin | ?tab=partner) ─────────
  // admin   → professionnels sous Gestion de Batilink Team (partner_name = id super_admin)
  // partner → professionnels sous Gestion Des Partenaires (non liés au super_admin)
  const scope = useMemo(() => {
    const t = new URLSearchParams(location.search).get("tab");
    return t === "admin" || t === "partner" ? t : null;
  }, [location.search]);

  const [superAdminId, setSuperAdminId] = useState(null);

  // Revenir à la première page quand on bascule entre les deux périmètres
  useEffect(() => { setPage(1); }, [scope]);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: adminData } = await supabase
        .from("admin_Profile")
        .select("role")
        .eq("user_id", user.id)
        .limit(1)
        .maybeSingle();
      if (active && adminData?.role === "super_admin") setSuperAdminId(user.id);
    })();
    return () => { active = false; };
  }, []);

  // ─── Export PDF (historique de paiement) ───────────────────────────────────
  const handleExportPaymentsPdf = () => {
    if (userDetails && userDetails.fees) {
      let feesData = [];
      try {
        feesData = typeof userDetails.fees === "string" ? JSON.parse(userDetails.fees) : userDetails.fees;
      } catch (e) {
        feesData = Array.isArray(userDetails.fees) ? userDetails.fees : [];
      }

      const payments = (feesData || []).map((f) => {
        const dateStr = f.created_at ? new Date(f.created_at).toLocaleDateString("fr-FR") : "";
        const startStr = f.period_start ? new Date(f.period_start).toLocaleDateString("fr-FR") : "";
        const endStr = f.period_end ? new Date(f.period_end).toLocaleDateString("fr-FR") : "";
        return {
          paymentDate: dateStr,
          start: startStr,
          end: endStr,
          paid: f.fees === true || f.fees === "true",
          price: Number(f.price || 0),
          ts: f.created_at ? new Date(f.created_at).getTime() : 0,
        };
      }).sort((a, b) => b.ts - a.ts);

      const totalPaid = payments.reduce((s, p) => s + (p.price || 0), 0);
      const paidCount = payments.filter(p => p.paid).length;
      const userName = `${userDetails.First_name || ""} ${userDetails.Last_name || ""}`.trim() || "utilisateur";

      const headerHtml = `<tr style="background:#fff7e6;">
        <th style="border:1px solid #ddd;padding:10px;text-align:left;">Date de paiement</th>
        <th style="border:1px solid #ddd;padding:10px;text-align:left;">Début période</th>
        <th style="border:1px solid #ddd;padding:10px;text-align:left;">Fin période</th>
        <th style="border:1px solid #ddd;padding:10px;text-align:left;">Statut</th>
        <th style="border:1px solid #ddd;padding:10px;text-align:right;">Prix (DT)</th>
      </tr>`;
      const rowsHtml = payments.map(p => `<tr>
        <td style="border:1px solid #ddd;padding:8px;">${p.paymentDate || "—"}</td>
        <td style="border:1px solid #ddd;padding:8px;">${p.start || "—"}</td>
        <td style="border:1px solid #ddd;padding:8px;">${p.end || "—"}</td>
        <td style="border:1px solid #ddd;padding:8px;color:${p.paid ? "#047857" : "#991b1b"};font-weight:600;">${p.paid ? "Payé" : "Non payé"}</td>
        <td style="border:1px solid #ddd;padding:8px;text-align:right;font-weight:600;">${p.price.toFixed(2)} DT</td>
      </tr>`).join("");

      const w = window.open("", "", "height=720,width=1100");
      if (!w) return;
      w.document.write(`
        <!doctype html><html><head><title>Historique de paiement - ${userName}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 24px; color: #222; }
          h1 { color: #fdd119; text-align: center; margin-bottom: 6px; }
          h2 { color: #555; text-align: center; margin-top: 0; }
          .meta { text-align: center; color: #888; margin: 6px 0 18px; font-size: 13px; }
          table { border-collapse: collapse; width: 100%; margin-top: 12px; font-size: 12px; }
          .total { margin-top: 14px; text-align: right; font-weight: 600; color: #222; }
          @media print { @page { size: A4 landscape; margin: 12mm; } }
        </style></head><body>
          <h1>BATILINK</h1>
          <h2>Historique de paiement</h2>
          <div class="meta">${userName} &middot; ${payments.length} paiement(s) &middot; ${paidCount} payé(s)</div>
          <table><thead>${headerHtml}</thead><tbody>${rowsHtml}</tbody></table>
          <div class="total">Total : ${totalPaid.toFixed(2)} DT</div>
        </body></html>
      `);
      w.document.close();
      w.focus();
      setTimeout(() => { try { w.print(); } catch (e) {} }, 300);
    }
  };

  // ─── Chargement des données ────────────────────────────────────────────────
  useEffect(() => {
    const loadPartners = async () => {
      setLoading(true);
      try {
        // Id de l'admin connecté : son profil "professional" sert uniquement à
        // publier → il ne doit jamais apparaître dans la liste des professionnels.
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        const currentUserId = currentUser?.id ? String(currentUser.id) : null;

        const [profilesRes, adminsRes, paymentStatusesRes] = await Promise.all([
          supabase
            .from("profiles")
            .select(`id, created_at, firstname, lastname, email, phone, governorate, is_activated, partner_name, role, imageavatar, profession, categories, website, linkedin, instagram, tiktok, reviews, Matricule`)
            .eq("role", "professional")
            .order("created_at", { ascending: false }),
          supabase.from("admin_Profile").select("user_id, First_name, Last_name, role"),
          supabase.rpc("get_professional_payment_statuses"),
        ]);

        const { data, error } = profilesRes;
        const { data: adminsData, error: adminsError } = adminsRes || {};
        const { data: paymentStatuses, error: paymentStatusesError } = paymentStatusesRes || {};

        if (error) {
          console.error("Erreur chargement clients :", error);
          message.error("Erreur chargement clients : " + (error.message || JSON.stringify(error)));
          setPartners([]);
          setError(error);
          return;
        }
        if (paymentStatusesError) throw paymentStatusesError;

        const paymentStatusByUserId = Object.fromEntries(
          (paymentStatuses || []).map(item => [String(item.user_id), item.payment_status])
        );

        // Comptes super_admin : ce sont les comptes "app" (ex. Batilink) qui ont
        // un profil professional uniquement pour publier → à exclure de la liste.
        const superAdminIds = new Set();
        if (!adminsError && Array.isArray(adminsData)) {
          const map = {};
          adminsData.forEach(a => {
            if (!a || !a.user_id) return;
            map[String(a.user_id)] = `${a.First_name || ""} ${a.Last_name || ""}`.trim();
            if ((a.role || "").replace(/\s+/g, " ").toLowerCase() === "super_admin") {
              superAdminIds.add(String(a.user_id));
            }
          });
          setAdminMap(map);
        }

        const mapped = (data || [])
          .filter((item) => !superAdminIds.has(String(item.id)) && String(item.id) !== currentUserId)
          .map((item) => ({
            ...item,
            First_name: item.firstname || "",
            Last_name: item.lastname || "",
            imageavatar: item.imageavatar || null,
            Matricule: item.Matricule ? String(item.Matricule).trim() : null,
            payment_status: paymentStatusByUserId[String(item.id)] || "unpaid",
            key: item.id,
          }));

        setPartners(mapped);
      } catch (err) {
        console.error("Erreur inattendue :", err);
        message.error("Erreur chargement clients : " + (err.message || JSON.stringify(err)));
        setPartners([]);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    loadPartners();
  }, []);

  // ─── Helpers affichage ────────────────────────────────────────────────────
  const resolvePartnerNames = (pn) => {
    if (!pn && pn !== 0) return "—";
    if (partnerIsAdmin(pn)) return "Admin";
    let ids = [];
    if (Array.isArray(pn)) ids = pn.map(String).map(s => s.trim()).filter(Boolean);
    else if (typeof pn === "string") ids = pn.split(/[,;|]+/).map(s => s.trim()).filter(Boolean);
    else ids = [String(pn).trim()].filter(Boolean);
    if (ids.length === 0) return "—";
    return ids.map(id => adminMap[String(id)] || id).join(", ");
  };

  // ─── Drawer : ouverture ───────────────────────────────────────────────────
  const showDrawer = async (record) => {
    setSelectedUser(record);
    setOpen(true);
    setLoadingUserDetails(true);

    try {
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select(`id, created_at, firstname, lastname, email, phone, governorate, is_activated, partner_name, role, imageavatar, profession, reviews, categories, website, linkedin, instagram, tiktok, fees, Matricule, activated_at, deactivated_at`)
        .eq("id", record.id)
        .single();

      if (profileError) throw profileError;

      let proDashboardData = null;
      if (profileData.role === "professional") {
        const { data: proRows, error: proError, status: proStatus } = await supabase
          .from("pro_dashboard")
          .select("urlGallery, rating, qualification, offered_services, address")
          .eq("user_id", profileData.id)
          .limit(1);

        if (!proError && Array.isArray(proRows) && proRows[0]) {
          proDashboardData = proRows[0];
        } else if (proError) {
          console.error("pro_dashboard supabase error", { proError, proStatus, userId: profileData.id });
        }
      }

      const formattedUser = {
        ...profileData,
        First_name: profileData.firstname,
        Last_name: profileData.lastname,
        imageavatar: profileData.imageavatar || null,
        address: proDashboardData?.address || null,
        urlGallery: proDashboardData?.urlGallery || null,
        rating: proDashboardData?.rating || null,
        qualification: proDashboardData?.qualification || null,
        offered_services: proDashboardData?.offered_services || null,
        Matricule: profileData.Matricule ? String(profileData.Matricule).trim() : null,
        payment_status: record.payment_status || "unpaid",
      };

      setUserDetails(formattedUser);
    } catch (err) {
      console.error("Erreur:", err);
      message.error("Erreur lors du chargement des détails : " + (err.message || JSON.stringify(err)));
    } finally {
      setLoadingUserDetails(false);
    }
  };

  const hideDrawer = () => {
    setOpen(false);
    setSelectedUser(null);
    setUserDetails(null);
    setIsAddingEcheance(false);
    setEcheanceForm({ activated_at: null, duration_years: 1 });
    setGalleryPage(1);
    setPaymentsPage(1);
  };

  // ─── Toggle activation ────────────────────────────────────────────────────
  const handleToggleActivation = async (record) => {
    Modal.confirm({
      title: record.is_activated ? "Désactiver le compte ?" : "Activer le compte ?",
      content: `Voulez-vous vraiment ${record.is_activated ? "désactiver" : "activer"} le compte de ${record.First_name} ${record.Last_name} ?`,
      okText: record.is_activated ? "Désactiver" : "Activer",
      cancelText: "Annuler",
      okButtonProps: { danger: record.is_activated },
      centered: true,
      onOk: async () => {
        try {
          const newStatus = !record.is_activated;
          const { data, error } = await supabase.from("profiles").update({ is_activated: newStatus }).eq("id", record.id).select();
          if (error) throw error;
          const updated = data && data[0];
          if (updated) {
            setPartners(prev => prev.map(p => (p.id === record.id ? { ...p, is_activated: newStatus, key: p.key } : p)));
            message.success(newStatus ? "Compte activé" : "Compte désactivé");
          }
        } catch (err) {
          message.error("Erreur lors de la modification: " + (err.message || JSON.stringify(err)));
        }
      },
    });
  };

  const handleOpenUserProfile = (record) => {
    const fullName = `${record?.First_name || ""} ${record?.Last_name || ""}`.trim();
    history.push(`/user/${record.id}`, { userName: fullName || "Utilisateur", userEmail: record.email });
  };

  const openNotificationModal = async (record) => {
    setNotificationText("");
    setNotificationTab("send");
    setNotificationModal({ open: true, record });

    const cachedHistory = notificationHistoryCache.current.get(record.id);
    if (cachedHistory) {
      setNotificationHistory(cachedHistory);
      return;
    }

    setNotificationHistory([]);

    const { data, error } = await supabase
      .from("profiles")
      .select("notifications")
      .eq("id", record.id)
      .single();

    if (error) {
      message.error("Impossible de charger l'historique d'envoi");
      return;
    }

    const notifications = Array.isArray(data?.notifications)
      ? data.notifications
      : data?.notifications && typeof data.notifications === "object"
      ? [data.notifications]
      : [];

    const history = notifications
      .filter(notification => notification?.type === "admin_broadcast")
      .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
    notificationHistoryCache.current.set(record.id, history);
    setNotificationHistory(history);
  };

  const closeNotificationModal = () => {
    if (notificationSending) return;
    setNotificationModal({ open: false, record: null });
    setNotificationText("");
    setNotificationHistory([]);
    setNotificationTab("send");
  };

  const handleSendNotification = async () => {
    const recipient = notificationModal.record;
    const description = notificationText.trim();
    if (!recipient?.id) {
      message.error("Utilisateur cible introuvable");
      return;
    }
    if (!description) {
      message.error("Veuillez saisir un message");
      return;
    }

    setNotificationSending(true);
    try {
      const { data: { user: sender }, error: senderError } = await supabase.auth.getUser();
      if (senderError || !sender?.id) throw senderError || new Error("Utilisateur connecté introuvable");

      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("notifications")
        .eq("id", recipient.id)
        .single();

      if (profileError) throw profileError;

      const existingNotifications = Array.isArray(profile?.notifications)
        ? profile.notifications
        : profile?.notifications && typeof profile.notifications === "object"
        ? [profile.notifications]
        : [];

      const newNotification = {
        read: false,
        type: "admin_broadcast",
        message: description,
        sender_id: sender.id,
        timestamp: new Date().toISOString(),
      };

      const { error: updateError } = await supabase
        .from("profiles")
        .update({
          notifications: [...existingNotifications, newNotification],
        })
        .eq("id", recipient.id);

      if (updateError) throw updateError;
      notificationHistoryCache.current.set(recipient.id, [
        newNotification,
        ...(notificationHistoryCache.current.get(recipient.id) || []),
      ]);
      message.success("Notification envoyée");
      setNotificationModal({ open: false, record: null });
      setNotificationText("");
      setNotificationHistory([]);
      setNotificationTab("send");
    } catch (err) {
      message.error(`Erreur lors de l'envoi : ${err.message || "opération impossible"}`);
    } finally {
      setNotificationSending(false);
    }
  };

  // ─── Preview paiement : calcul prix + promo avant confirmation ────────────
  const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

  const normalizeProfessionForLookup = (raw) => {
    const n = (raw || "").toString().trim().toLowerCase();
    const aliases = {
      artisant: "artisan",
      artisans: "artisan",
      company: "entreprise",
      companie: "entreprise",
      compagnie: "entreprise",
      societe: "entreprise",
      "société": "entreprise",
      concepteurs: "concepteur",
      fournisseurs: "fournisseur",
    };
    return aliases[n] || n;
  };

  const openPaymentPreview = async () => {
    const { activated_at, duration_years } = echeanceForm;
    if (!activated_at) {
      message.error("Veuillez choisir une date de début");
      return;
    }
    if (!userDetails) {
      message.error("Détails utilisateur indisponibles");
      return;
    }

    setPaymentPreview(prev => ({ ...prev, open: true, loading: true, years: duration_years }));

    try {
      // 1) Prix de base via profession (avec normalisation des typos)
      const professionRaw = userDetails.profession || "";
      const profNorm = normalizeProfessionForLookup(professionRaw);
      let basePrice = 0;

      if (profNorm) {
        // Match exact
        const { data: catExact } = await supabase
          .from("category")
          .select("price")
          .ilike("title", profNorm)
          .limit(1);
        if (catExact && catExact.length > 0) {
          basePrice = Number(catExact[0].price) || 0;
        }
        // Fallback : préfixe commun
        if (basePrice === 0) {
          const { data: catLike } = await supabase
            .from("category")
            .select("price, title")
            .or(`title.ilike.${profNorm}%,title.ilike.%${profNorm}%`)
            .limit(1);
          if (catLike && catLike.length > 0) {
            basePrice = Number(catLike[0].price) || 0;
          }
        }
      }

      // 2) Promo via partner_name → applies_to du sous_admin
      const partnerRaw = (userDetails.partner_name || "").toString().trim().toLowerCase();
      let promoId = null;
      let discount = 0;
      let partnerId = null;

      if (partnerRaw && partnerRaw !== "admin" && UUID_RE.test(partnerRaw)) {
        partnerId = partnerRaw;
        const nowIso = new Date().toISOString();
        const { data: promoRows } = await supabase
          .from("promo_codes")
          .select("id, discount, expires_at, is_active, created_at, applies_to")
          .eq("applies_to", partnerId)
          .eq("is_active", true)
          .lte("created_at", nowIso)
          .or(`expires_at.is.null,expires_at.gte.${nowIso}`)
          .order("created_at", { ascending: false })
          .limit(1);
        if (promoRows && promoRows.length > 0) {
          const d = Number(promoRows[0].discount) || 0;
          if (d > 0 && d <= 100) {
            promoId = promoRows[0].id;
            discount = d;
          }
        }
      }

      const finalPrice = discount > 0
        ? Math.round(basePrice * (100 - discount)) / 100
        : basePrice;
      const total = Math.round(finalPrice * duration_years * 100) / 100;
      const totalOriginal = Math.round(basePrice * duration_years * 100) / 100;

      setPaymentPreview({
        open: true,
        loading: false,
        basePrice,
        finalPrice,
        discount,
        promoId,
        years: duration_years,
        total,
        totalOriginal,
        profession: professionRaw,
      });
    } catch (err) {
      console.error("Erreur preview paiement:", err);
      message.error("Erreur lors du calcul du tarif");
      setPaymentPreview(prev => ({ ...prev, open: false, loading: false }));
    }
  };

  const closePaymentPreview = () => {
    setPaymentPreview(prev => ({ ...prev, open: false }));
  };

  // ─── handleSaveEcheance ───────────────────────────────────────────────────
  // L'admin choisit : date de début + nombre d'ANNÉES
  // deactivated_at = activated_at + N années EXACTEMENT
  // Le trigger SQL reçoit des dates exactes → génère exactement N fees (1 par an)
  const handleSaveEcheance = async () => {
    const { activated_at, duration_years } = echeanceForm;

    if (!activated_at) {
      message.error("Veuillez choisir une date de début");
      return;
    }

    const targetId = selectedUser?.id || echeanceModal?.record?.id;
    if (!targetId) {
      message.error("Utilisateur cible introuvable");
      return;
    }

    // Calcul exact : start + N années
    const startDate = activated_at.toDate ? activated_at.toDate() : new Date(activated_at);
    const endDate = addYears(startDate, duration_years);

    // Vérifier les doublons : check chaque année qui sera couverte
    if (userDetails?.fees) {
      let feesArr = [];
      try {
        feesArr = typeof userDetails.fees === "string"
          ? JSON.parse(userDetails.fees)
          : (Array.isArray(userDetails.fees) ? userDetails.fees : []);
      } catch (e) {
        feesArr = [];
      }

      // Générer la liste des années à couvrir (clé = "YYYY")
      const yearsToCover = [];
      for (let i = 0; i < duration_years; i++) {
        const d = addYears(startDate, i);
        yearsToCover.push(String(d.getFullYear()));
      }

      const alreadyPaid = feesArr.some(
        f => yearsToCover.includes(String(f.year || (f.month || '').split('-')[0])) && (f.fees === true || f.fees === "true")
      );

      if (alreadyPaid) {
        message.warning("Une ou plusieurs années sont déjà enregistrées dans l'historique.");
        return;
      }
    }

    try {
      // ⚠️ NE PAS faire MAX(deactivated_at, endDate) ici : ça étendrait la plage
      // transmise au trigger qui générerait des fees pour TOUS les mois entre
      // startDate et l'ancien deactivated_at. La conservation du "service end"
      // cumulatif se fait côté trigger (max(period_end) sur l'ensemble des fees).
      const payload = {
        activated_at: startDate.toISOString(),
        deactivated_at: endDate.toISOString(),
      };

      const { data, error } = await supabase
        .from("profiles")
        .update(payload)
        .eq("id", targetId)
        .select();

      if (error) {
        console.error("Erreur mise à jour échéance :", error);
        message.error("Erreur lors de l'enregistrement");
        return;
      }

      const updated = data && data[0];
      if (updated) {
        setUserDetails(prev => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
        setPartners(prev => prev.map(p => (p.id === updated.id ? { ...p, ...updated, key: updated.id } : p)));

        const { data: paymentStatuses, error: paymentStatusError } = await supabase
          .rpc("get_professional_payment_statuses");
        if (paymentStatusError) {
          console.error("Erreur actualisation statut paiement :", paymentStatusError);
        } else {
          const paymentStatus = (paymentStatuses || []).find(
            item => String(item.user_id) === String(updated.id)
          )?.payment_status;
          if (paymentStatus) {
            setPartners(prev => prev.map(p =>
              p.id === updated.id ? { ...p, payment_status: paymentStatus } : p
            ));
            setUserDetails(prev => prev && prev.id === updated.id
              ? { ...prev, payment_status: paymentStatus }
              : prev);
          }
        }
      }

      message.success(`${duration_years} ${duration_years > 1 ? 'années enregistrées' : 'année enregistrée'} avec succès`);
      setIsAddingEcheance(false);
      setEcheanceForm({ activated_at: null, duration_years: 1 });
      setPaymentPreview(prev => ({ ...prev, open: false }));
    } catch (err) {
      console.error("Erreur inattendue :", err);
      message.error("Erreur lors de l'ajout : " + (err.message || JSON.stringify(err)));
    }
  };

  // ─── Promotion partenaire ──────────────────────────────────────────────────
  const refreshAdminMap = async () => {
    try {
      const { data, error } = await supabase.from("admin_Profile").select("user_id, First_name, Last_name");
      if (error) return;
      const map = {};
      (data || []).forEach(a => { if (a && a.user_id) map[String(a.user_id)] = `${a.First_name || ""} ${a.Last_name || ""}`.trim(); });
      setAdminMap(map);
    } catch {}
  };

  const openPromoteModal = (record) => {
    setSelectedProForPromotion(record);
    setPromoteCode("");
    setPromoteCodeCheck({ status: "idle", message: "" });
    setPromoteCodeSuggestions({ loading: false, items: [] });
    setPromoteModalOpen(true);
  };

  const closePromoteModal = () => {
    if (promotingId) return;
    setPromoteModalOpen(false);
    setSelectedProForPromotion(null);
    setPromoteCode("");
    setPromoteCodeCheck({ status: "idle", message: "" });
    setPromoteCodeSuggestions({ loading: false, items: [] });
  };

  useEffect(() => {
    if (!promoteModalOpen) return;
    const code = (promoteCode || "").toString().trim();
    if (!code) { setPromoteCodeCheck({ status: "idle", message: "" }); setPromoteCodeSuggestions({ loading: false, items: [] }); return; }
    if (!/^\d{3}$/.test(code)) { setPromoteCodeCheck({ status: "invalid", message: "Le code doit contenir exactement 3 chiffres" }); setPromoteCodeSuggestions({ loading: false, items: [] }); return; }
    setPromoteCodeCheck({ status: "checking", message: "Vérification…" });
    setPromoteCodeSuggestions({ loading: false, items: [] });
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { data, error } = await supabase.from("admin_Profile").select("user_id").eq("referred_by_code", code).maybeSingle();
        if (cancelled) return;
        if (error) setPromoteCodeCheck({ status: "error", message: "Erreur de vérification" });
        else if (data) {
          setPromoteCodeCheck({ status: "taken", message: "Ce code est déjà utilisé" });
          setPromoteCodeSuggestions({ loading: true, items: [] });
          const items = await fetchAvailableSuggestions(code, 3);
          if (!cancelled) setPromoteCodeSuggestions({ loading: false, items });
        }
        else setPromoteCodeCheck({ status: "available", message: "Code disponible" });
      } catch (e) {
        if (!cancelled) setPromoteCodeCheck({ status: "error", message: "Erreur de vérification" });
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [promoteCode, promoteModalOpen]);

  const promoteToPartner = async (record) => {
    if (!record?.id) return;
    const code = (promoteCode || "").toString().trim();
    if (!code) { message.error("Veuillez saisir un code de parrainage."); return; }
    if (!/^\d{3}$/.test(code)) { message.error("Le code de parrainage doit contenir exactement 3 chiffres."); return; }
    setPromotingId(record.id);
    try {
      const defaultPercent = await getDefaultPartnerPercent();
      const { error } = await supabase.rpc("promote_pro_to_partner", { p_target_user_id: record.id, p_percent: defaultPercent, p_salary: [], p_fees: [], p_referred_by_code: code });
      if (error) throw error;
      // Filet de sécurité : forcer le percent au cas où la fonction SQL l'ignore
      await supabase
        .from('admin_Profile')
        .update({ percent: defaultPercent })
        .eq('user_id', record.id);
      notification.success({ message: "Pro promu en partenaire", description: `${record.First_name || ""} ${record.Last_name || ""} est désormais partenaire.`.trim(), duration: 4 });
      setPromoteModalOpen(false);
      setSelectedProForPromotion(null);
      setPromoteCode("");
      setPromoteCodeCheck({ status: "idle", message: "" });
      setPromoteCodeSuggestions({ loading: false, items: [] });
      notifyPartnerRequestsChanged();
      await refreshAdminMap();
    } catch (err) {
      const raw = err?.message || JSON.stringify(err);
      if (raw.includes("REFERRAL_CODE_TAKEN")) message.error("Ce code est déjà utilisé.");
      else if (raw.includes("REFERRAL_CODE_INVALID_FORMAT")) message.error("Le code doit contenir exactement 3 chiffres.");
      else if (raw.includes("ALREADY_PARTNER")) message.error("Ce professionnel est déjà partenaire.");
      else if (raw.includes("TARGET_NOT_A_PROFESSIONAL")) message.error("La cible n'est pas un professionnel.");
      else if (raw.includes("NOT_AUTHORIZED")) message.error("Vous n'avez pas les droits pour cette action.");
      else message.error(raw.length < 200 ? raw : "Erreur lors de la promotion.");
    } finally {
      setPromotingId(null);
    }
  };

  // ─── Colonnes table ────────────────────────────────────────────────────────
  const columns = [
    {
      title: "Client",
      dataIndex: "imageavatar",
      key: "imageavatar",
      width: 200,
      render: (value, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }} onClick={() => showDrawer(record)}>
          <Avatar
            src={value && String(value).trim() !== "" ? value : undefined}
            size={36}
            style={{ borderRadius: "50%", background: !(value && String(value).trim() !== "") ? avatarColorFor(record.id || record.email || record.First_name) : undefined, color: "#ffffff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 18 }}
          >
            {!(value && String(value).trim() !== "") ? getInitials(record.First_name, record.Last_name) : null}
          </Avatar>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span
              style={{ textDecoration: "none", transition: "color 0.2s ease", display: "flex", alignItems: "center", gap: 6 }}
              onMouseEnter={e => (e.target.style.color = "#40a9ff")}
              onMouseLeave={e => (e.target.style.color = "#1890ff")}
            >
              {record.First_name} {record.Last_name}
              <VerifiedBadge matricule={record.Matricule} size={17} />
            </span>
            <span style={{ color: "#666", fontSize: "12px" }}>{record.governorate}</span>
          </div>
        </div>
      ),
      responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
    },
    {
      title: "PROFESSION",
      key: "profession",
      width: 240,
      render: (_, record) => {
        const cats = parseCategories(record.categories);
        const catsDisplay = cats.length > 1 ? `${cats[0]}...` : (cats[0] || "");
        const professionLabels = { company: "Entreprise", artisant: "Artisan" };
        const professionRaw = String(record.profession || "").trim();
        const professionDisplay = professionLabels[professionRaw.toLowerCase()] || professionRaw;
        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ color: "#000", fontWeight: 600, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={catsDisplay}>{catsDisplay}</span>
            <span style={{ color: "#666", fontSize: 12 }}>{professionDisplay}</span>
          </div>
        );
      },
      responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
    },
    {
      title: "PARTENAIRE",
      key: "partner",
      width: 180,
      render: (_, record) => {
        const isAdm = partnerIsAdmin(record?.partner_name);
        return <Tag color={isAdm ? "gold" : "blue"} style={{ borderRadius: 12, padding: "4px 10px", fontWeight: 500 }}>{resolvePartnerNames(record?.partner_name)}</Tag>;
      },
      responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
    },
    {
      title: "Paiement",
      key: "payment_status",
      width: 130,
      align: "center",
      render: (_, record) => {
        const isPaid = record.payment_status === "paid";
        return <Tag color={isPaid ? "green" : "red"}>{isPaid ? "Payé" : "Non payé"}</Tag>;
      },
      responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
    },
    {
      title: "État du compte",
      key: "status",
      width: 150,
      align: "center",
      render: (_, record) => (
        <Select
          value={record.is_activated ? "Activer" : "Désactiver"}
          onChange={() => handleToggleActivation(record)}
          style={{ width: "100%", borderRadius: 8 }}
          options={[
            { value: "Activer", label: <span style={{ color: "#15803d", fontWeight: 600 }}>Activer</span> },
            { value: "Désactiver", label: <span style={{ color: "#dc2626", fontWeight: 600 }}>Désactiver</span> },
          ]}
        />
      ),
      responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
    },
    {
      title: "Actions",
      key: "actions",
      width: 130,
      align: "center",
      render: (_, record) => {
        const isAlreadyPartner = !!adminMap[String(record.id)];
        return (
          <div style={{ display: "flex", gap: 6, justifyContent: "center", alignItems: "center" }}>
            <Tooltip title="Voir le profil">
              <Button size="small" onClick={e => { e.stopPropagation(); showDrawer(record); }}>Voir</Button>
            </Tooltip>
            <Tooltip title="Envoyer une notification">
              <Button
                style={{ paddingLeft: 5}}
                icon={<NotificationOutlined />}
                aria-label={`Envoyer une notification à ${record.First_name || "cet utilisateur"}`}
                onClick={e => { e.stopPropagation(); openNotificationModal(record); }}
              />
            </Tooltip>
            {isAlreadyPartner ? (
              <Tooltip title="Déjà partenaire">
                <Button size="small" icon={<CheckOutlined />} disabled style={{ background: "#06df4e", borderColor: "#06df4e", color: "#1a1a1a", fontWeight: 600 }}>Partenaire</Button>
              </Tooltip>
            ) : (
              <Tooltip title="Promouvoir en partenaire">
                <Button size="small" icon={<UserAddOutlined />} onClick={e => { e.stopPropagation(); openPromoteModal(record); }} style={{ background: "#fdd119", borderColor: "#fdd119", color: "#1a1a1a", fontWeight: 600 }}>Upgrade</Button>
              </Tooltip>
            )}
          </div>
        );
      },
      responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
    },
  ];

  // ─── Filtres ───────────────────────────────────────────────────────────────
  const getFilteredClients = () => {
    let filtered = clientss || [];
    // Périmètre de gestion (depuis les cartes du tableau de bord)
    if (scope && superAdminId) {
      if (scope === "admin") {
        filtered = filtered.filter(c => normalizePartnerIds(c?.partner_name).includes(superAdminId));
      } else if (scope === "partner") {
        filtered = filtered.filter(c => !normalizePartnerIds(c?.partner_name).includes(superAdminId));
      }
    }
    if (partnerFilter === "admin") {
      filtered = filtered.filter(client => partnerIsAdmin(client?.partner_name));
    } else if (partnerFilter !== "all") {
      filtered = filtered.filter(client => {
        const pn = client?.partner_name;
        if (!pn && pn !== 0) return false;
        if (Array.isArray(pn)) return pn.map(String).map(s => s.trim()).includes(partnerFilter);
        if (typeof pn === "string") return pn.split(/[,;|]+/).map(s => s.trim()).includes(partnerFilter);
        return String(pn).trim() === partnerFilter;
      });
    }
    if (statusFilter === "Activé") filtered = filtered.filter(c => c?.is_activated === true);
    else if (statusFilter === "Désactivé") filtered = filtered.filter(c => c?.is_activated === false);
    if (governorateFilter !== "all") filtered = filtered.filter(c => c?.governorate === governorateFilter);
    if (search) {
      // Normalise : minuscules + suppression des accents (NFD + retrait des
      // marques diacritiques) → "Éric" devient "eric", "ç" devient "c".
      const norm = (s) => String(s || "")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .trim();
      const q = norm(search);
      const professionLabels = { company: "entreprise", artisant: "artisan" };
      filtered = filtered.filter(p => {
        const first = norm(p?.First_name);
        const last = norm(p?.Last_name);
        const email = norm(p?.email);
        const gov = norm(p?.governorate);
        const profRaw = norm(p?.profession);
        // Match aussi le label traduit ("Entreprise" matche "company", "Artisan" matche "artisant")
        const profLabel = norm(professionLabels[profRaw] || profRaw);
        const partner = norm(resolvePartnerNames(p?.partner_name));
        // Catégories : parseCategories gère string / JSON / array de manière uniforme
        const cats = parseCategories(p?.categories) || [];
        const catsTokens = cats.map(c => norm(c));
        const catsJoined = catsTokens.join(", ");
        return (
          first.includes(q) ||
          last.includes(q) ||
          email.includes(q) ||
          gov.includes(q) ||
          profRaw.includes(q) ||
          profLabel.includes(q) ||
          partner.includes(q) ||
          catsJoined.includes(q) ||
          catsTokens.some(t => t.includes(q))
        );
      });
    }
    return filtered;
  };

  const filteredPartners = getFilteredClients();
  const pagedPartners = filteredPartners.slice((page - 1) * pageSize, page * pageSize);
  const totalPages = Math.ceil(filteredPartners.length / pageSize) || 1;

  // ─── KPI : total / sous Batilink Team / sous Partenaires ──────────────────
  const kpiStats = useMemo(() => {
    const all = clientss || [];
    const total = all.length;
    const batilink = superAdminId
      ? all.filter(c => normalizePartnerIds(c?.partner_name).includes(superAdminId)).length
      : 0;
    const partners = superAdminId ? total - batilink : 0;
    return { total, batilink, partners };
  }, [clientss, superAdminId]);

  // ─── Export PDF (liste des professionnels filtrée) ────────────────────────
  const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const handleExportUsersPdf = () => {
    const professionLabels = { company: "Entreprise", artisant: "Artisan" };
    const headerHtml = `<tr style="background:#fff7e6;">
      <th style="border:1px solid #ddd;padding:10px;text-align:left;">Nom</th>
      <th style="border:1px solid #ddd;padding:10px;text-align:left;">Email</th>
      <th style="border:1px solid #ddd;padding:10px;text-align:left;">Téléphone</th>
      <th style="border:1px solid #ddd;padding:10px;text-align:left;">Gouvernorat</th>
      <th style="border:1px solid #ddd;padding:10px;text-align:left;">Profession</th>
      <th style="border:1px solid #ddd;padding:10px;text-align:left;">Catégories</th>
      <th style="border:1px solid #ddd;padding:10px;text-align:left;">Partenaire</th>
      <th style="border:1px solid #ddd;padding:10px;text-align:center;">État</th>
    </tr>`;
    const rowsHtml = filteredPartners.map((r) => {
      const fullName = `${r.First_name || ''} ${r.Last_name || ''}`.trim() || '—';
      const cats = parseCategories(r.categories);
      const catsStr = cats.length > 0 ? cats.join(' · ') : '—';
      const professionRaw = String(r.profession || '').trim();
      const professionLabel = professionLabels[professionRaw.toLowerCase()] || professionRaw || '—';
      const partnerLabel = resolvePartnerNames(r.partner_name) || '—';
      const status = r.is_activated ? 'Activé' : 'Désactivé';
      const statusColor = r.is_activated ? '#047857' : '#991b1b';
      return `<tr>
        <td style="border:1px solid #ddd;padding:8px;font-weight:600;">${escapeHtml(fullName)}</td>
        <td style="border:1px solid #ddd;padding:8px;">${escapeHtml(r.email || '—')}</td>
        <td style="border:1px solid #ddd;padding:8px;">${escapeHtml(r.phone || '—')}</td>
        <td style="border:1px solid #ddd;padding:8px;">${escapeHtml(r.governorate || '—')}</td>
        <td style="border:1px solid #ddd;padding:8px;">${escapeHtml(professionLabel)}</td>
        <td style="border:1px solid #ddd;padding:8px;">${escapeHtml(catsStr)}</td>
        <td style="border:1px solid #ddd;padding:8px;">${escapeHtml(partnerLabel)}</td>
        <td style="border:1px solid #ddd;padding:8px;text-align:center;color:${statusColor};font-weight:600;">${status}</td>
      </tr>`;
    }).join('');

    const filters = [];
    if (search) filters.push(`recherche : ${search}`);
    if (statusFilter !== 'all') filters.push(`état : ${statusFilter}`);
    if (governorateFilter !== 'all') filters.push(`gouvernorat : ${governorateFilter}`);
    if (partnerFilter !== 'all') filters.push(`partenaire : ${adminMap[partnerFilter] || partnerFilter}`);
    const filtersLine = filters.length ? filters.join(' · ') : 'aucun filtre';

    const w = window.open('', '', 'height=720,width=1200');
    if (!w) return;
    w.document.write(`
      <!doctype html><html><head><title>Liste des professionnels</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 24px; color: #222; }
        h1 { color: #fdd119; text-align: center; margin-bottom: 6px; }
        h2 { color: #555; text-align: center; margin-top: 0; }
        .meta { text-align: center; color: #888; margin: 6px 0 18px; font-size: 13px; }
        table { border-collapse: collapse; width: 100%; margin-top: 12px; font-size: 11px; }
        @media print { @page { size: A4 landscape; margin: 10mm; } }
      </style></head><body>
        <h1>BATILINK</h1>
        <h2>Liste des professionnels</h2>
        <div class="meta">${filteredPartners.length} résultat(s) &middot; ${escapeHtml(filtersLine)}</div>
        <table><thead>${headerHtml}</thead><tbody>${rowsHtml}</tbody></table>
      </body></html>
    `);
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch (e) {} }, 300);
  };

  // ─── Parsers locaux ────────────────────────────────────────────────────────
  const parseServicesLocal = (offered_services) => {
    if (!offered_services) return [];
    if (typeof offered_services === "string") { try { return JSON.parse(offered_services); } catch { return offered_services.split(",").map(s => s.trim()); } }
    if (Array.isArray(offered_services)) return offered_services;
    return [offered_services];
  };

  // ─── Render paiements (onglet Historique) ─────────────────────────────────
  const renderPaymentsHistory = () => {
    let payments = [];
    if (userDetails?.fees) {
      try {
        const feesData = typeof userDetails.fees === "string" ? JSON.parse(userDetails.fees) : userDetails.fees;
        if (Array.isArray(feesData)) {
          payments = feesData.map(fee => {
            // Priorité : period_start/period_end stockés par le trigger SQL.
            // Fallback : déduits depuis fee.month (premier jour du mois +1 mois).
            // Dernier recours : created_at + 1 mois.
            let startD = fee.period_start ? new Date(fee.period_start) : null;
            let endD = fee.period_end ? new Date(fee.period_end) : null;
            if (!startD || isNaN(startD.getTime())) {
              if (fee.month && /^\d{4}-\d{1,2}$/.test(String(fee.month))) {
                const [y, m] = String(fee.month).split("-").map(Number);
                startD = new Date(Date.UTC(y, m - 1, 1));
              } else if (fee.created_at) {
                startD = new Date(fee.created_at);
              }
            }
            if (!endD || isNaN(endD.getTime())) {
              endD = startD ? addMonths(new Date(startD), 1) : null;
            }
            return {
              ...fee,
              year: fee.month ? parseInt(String(fee.month).split("-")[0], 10) : new Date().getFullYear(),
              monthKey: fee.month ? String(fee.month) : "",
              monthName: fee.month ? getMonthName(fee.month) : "Inconnu",
              price: fee.price || 0,
              created_at: fee.created_at || null,
              start_date: startD,
              end_date: endD,
              fees: Boolean(fee.fees),
            };
          });
        }
      } catch (e) {}
    }

    // Tri par date de paiement (created_at), du plus récent au plus ancien
    const sorted = [...payments].sort((a, b) => {
      const da = a.created_at ? new Date(a.created_at).getTime() : 0;
      const db = b.created_at ? new Date(b.created_at).getTime() : 0;
      return db - da;
    });

    const totalAll = sorted.reduce((s, p) => s + (p.price || 0), 0);
    const paidCount = sorted.filter(p => p.fees).length;
    const totalPaymentsPages = Math.max(1, Math.ceil(sorted.length / paymentsPageSize));
    const currentPage = Math.min(paymentsPage, totalPaymentsPages);
    const paged = sorted.slice((currentPage - 1) * paymentsPageSize, currentPage * paymentsPageSize);

    return (
      <div style={{ padding: "24px" }}>
        {/* Header section */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <Text style={{ color: "#64748b", fontSize: 13, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Payement
          </Text>
          {!isAddingEcheance && (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                // Date de début = fin du dernier paiement (period_end, ou created_at + 1 mois en fallback)
                let nextStartDate = null;
                if (userDetails?.fees) {
                  try {
                    const feesData = typeof userDetails.fees === "string" ? JSON.parse(userDetails.fees) : userDetails.fees;
                    if (Array.isArray(feesData) && feesData.length > 0) {
                      let latestEnd = null;
                      feesData.forEach(fee => {
                        let endD = fee.period_end ? new Date(fee.period_end) : null;
                        if ((!endD || isNaN(endD.getTime())) && fee.created_at) {
                          endD = addMonths(new Date(fee.created_at), 1);
                        }
                        if (endD && !isNaN(endD.getTime())) {
                          if (!latestEnd || endD > latestEnd) latestEnd = endD;
                        }
                      });
                      if (latestEnd) nextStartDate = latestEnd;
                    }
                  } catch (e) {}
                }
                // Fallback : premier paiement → date du jour
                if (!nextStartDate) {
                  nextStartDate = new Date();
                  nextStartDate.setHours(0, 0, 0, 0);
                }
                setIsAddingEcheance(true);
                const dayjs = require('dayjs');
                setEcheanceForm({ activated_at: dayjs(nextStartDate), duration_years: 1 });
              }}
              style={{ background: "linear-gradient(135deg, #eae166ff 0%, #fdd119 100%)", borderRadius: 8, fontWeight: 600, borderColor: "#fdd119" }}
            >
              Payer
            </Button>
          )}
        </div>

        {/* ─── FIX : Formulaire de paiement ─────────────────────────────── */}
        {isAddingEcheance && (
          <div style={{ background: "#fff", padding: "20px", borderRadius: 12, border: "1px solid #e2e8f0", marginBottom: 16 }}>

            {/* Champ 1 : Date de début (pré-remplie = fin du dernier paiement, jours payés bloqués) */}
            <div style={{ marginBottom: 14 }}>
              <Text style={{ color: "#374151", fontSize: 13, display: "block", marginBottom: 8, fontWeight: 600 }}>
                Date de début
              </Text>
              <DatePicker
                size="middle"
                style={{ width: "100%", borderRadius: 8 }}
                value={echeanceForm.activated_at}
                defaultPickerValue={echeanceForm.activated_at}
                onChange={date => setEcheanceForm(prev => ({ ...prev, activated_at: date }))}
                placeholder="Choisir la date de début"
                format="DD/MM/YYYY"
                allowClear={false}
                disabledDate={(current) => {
                  if (!current) return false;
                  const currentDate = current.toDate ? current.toDate() : new Date(current);
                  currentDate.setHours(0, 0, 0, 0);

                  // Bloque uniquement les jours qui tombent dans une période déjà payée
                  if (userDetails?.fees) {
                    try {
                      const feesData = typeof userDetails.fees === "string" ? JSON.parse(userDetails.fees) : userDetails.fees;
                      if (Array.isArray(feesData)) {
                        for (const fee of feesData) {
                          if (fee.fees !== true && fee.fees !== "true") continue;
                          let periodStart = fee.period_start ? new Date(fee.period_start) : (fee.created_at ? new Date(fee.created_at) : null);
                          let periodEnd = fee.period_end ? new Date(fee.period_end) : (periodStart ? addMonths(periodStart, 1) : null);
                          if (!periodStart || !periodEnd || isNaN(periodStart.getTime()) || isNaN(periodEnd.getTime())) continue;
                          periodStart.setHours(0, 0, 0, 0);
                          periodEnd.setHours(0, 0, 0, 0);
                          if (currentDate >= periodStart && currentDate < periodEnd) return true;
                        }
                      }
                    } catch (e) {}
                  }
                  return false;
                }}
              />
              <Text style={{ color: "#94a3b8", fontSize: 11, display: "block", marginTop: 4 }}>
                Pré-remplie à la fin du dernier paiement. Les jours déjà payés sont désactivés.
              </Text>
            </div>

            {/* Champ 2 : Nombre d'années */}
            <div style={{ marginBottom: 14 }}>
              <Text style={{ color: "#374151", fontSize: 13, display: "block", marginBottom: 8, fontWeight: 600 }}>
                Nombre d'années
              </Text>
              <Select
                size="middle"
                style={{ width: "100%", borderRadius: 8 }}
                value={echeanceForm.duration_years}
                onChange={val => setEcheanceForm(prev => ({ ...prev, duration_years: val }))}
                options={[1, 2, 3, 4, 5].map(n => ({ value: n, label: `${n} ${n > 1 ? 'ans' : 'an'}` }))}
              />
            </div>

            {/* Aperçu calculé automatiquement */}
            {echeanceForm.activated_at && computedEndDate && (
              <div style={{ padding: "10px 14px", background: "#fffbe6", border: "1px solid #fdd119", borderRadius: 8, marginBottom: 14, fontSize: 13, color: "#555" }}>
                 Du{" "}
                <strong>
                  {(echeanceForm.activated_at.toDate
                    ? echeanceForm.activated_at.toDate()
                    : new Date(echeanceForm.activated_at)
                  ).toLocaleDateString("fr-FR")}
                </strong>
                {" "}au{" "}
                <strong>{computedEndDate.toLocaleDateString("fr-FR")}</strong>
                {" "}→{" "}
                <strong>{echeanceForm.duration_years} {echeanceForm.duration_years > 1 ? 'ans' : 'an'}</strong>
              </div>
            )}

            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <Button
                size="middle"
                icon={<CloseCircleOutlined />}
                onClick={() => {
                  setIsAddingEcheance(false);
                  setEcheanceForm({ activated_at: null, duration_years: 1 });
                }}
                style={{ borderRadius: 8, fontWeight: 600 }}
              >
                Annuler
              </Button>
              <Button
                size="middle"
                icon={<CheckOutlined />}
                onClick={openPaymentPreview}
                style={{ borderRadius: 8, fontWeight: 600, borderColor: "#fdd119", background: "#fdd119" }}
              >
                Ajouter
              </Button>
            </div>
          </div>
        )}

        {/* Stat card */}
        {!isAddingEcheance && (
          <div style={{ marginTop: 0, padding: "16px", background: "#FFF", borderRadius: 12, border: "1px solid #222", marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Text style={{ color: "#1e293b", fontSize: 15, fontWeight: 600 }}>Total payé</Text>
              <Text style={{ color: "#10b981", fontSize: 18, fontWeight: 700 }}>{totalAll} DT</Text>
            </div>
            <div style={{ marginTop: 8, display: "flex", gap: 40, color: "#64748b", fontSize: 14 }}>
              <span>Nombre de paiements : {paidCount}</span>
            </div>
          </div>
        )}

        {/* Historique */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20, marginBottom: 10 }}>
          <Text style={{ color: "#222", fontSize: 14, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Historique des paiements
          </Text>
          <Button
            icon={<FilePdfOutlined style={{ color: "#c0392b" }} />}
            onClick={handleExportPaymentsPdf}
            style={{ borderColor: "#222", color: "#222", fontWeight: 600, marginBottom: 10 }}
          >
            Export PDF
          </Button>
        </div>

        {sorted.length === 0 ? (
          <div style={{ background: "#fff", padding: "10px 5px", borderRadius: 12, border: "1px solid #222", textAlign: "center" }}>
            <DollarOutlined style={{ fontSize: 30, color: "green", marginBottom: 10, marginRight: "10px" }} />
            <Text style={{ color: "#222", fontSize: 14 }}>Aucun paiement</Text>
          </div>
        ) : (
          <>
            <Timeline
              style={{ marginLeft: 6 }}
              items={paged.map(payment => {
                const paymentDateLabel = payment.created_at
                  ? new Date(payment.created_at).toLocaleDateString("fr-FR")
                  : "Date inconnue";
                return {
                  color: payment.fees ? "green" : "red",
                  children: (
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
                      <div style={{ flex: 1 }}>
                        <Text style={{ color: "#1e293b", fontSize: 15, fontWeight: 700 }}>
                          Date de paiement : {paymentDateLabel}
                        </Text>
                        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, borderRadius: "50%", background: payment.fees ? "#d1fae5" : "#fee2e2", fontSize: 11, fontWeight: 600, color: payment.fees ? "#047857" : "#991b1b" }}>
                              {payment.fees ? "✓" : "✗"}
                            </span>
                            <Text style={{ fontSize: 13, color: payment.fees ? "#047857" : "#991b1b", fontWeight: 600 }}>
                              {payment.fees ? "Payé" : "Non payé"}
                            </Text>
                          </div>
                          {(payment.start_date || payment.end_date) && (
                            <div style={{ display: "flex", alignItems: "center", gap: 24, marginLeft: 28, flexWrap: "wrap" }}>
                              <Text style={{ fontSize: 12, color: "#64748b" }}>
                                Période :{" "}
                                {payment.start_date ? payment.start_date.toLocaleDateString("fr-FR") : "—"}
                                {" → "}
                                {payment.end_date ? payment.end_date.toLocaleDateString("fr-FR") : "—"}
                              </Text>
                            </div>
                          )}
                        </div>
                      </div>
                      <Text style={{ color: payment.fees ? "#10b981" : "#ef4444", fontSize: 18, fontWeight: 700, flexShrink: 0, minWidth: 80, textAlign: "right" }}>
                        {payment.price} DT
                      </Text>
                    </div>
                  ),
                };
              })}
            />
            {totalPaymentsPages > 1 && (
              <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 12, gap: 12 }}>
                <Button size="small" disabled={currentPage === 1} onClick={() => setPaymentsPage(currentPage - 1)}>Précédent</Button>
                <Text style={{ color: "#666", fontSize: 13 }}>Page {currentPage} / {totalPaymentsPages}</Text>
                <Button size="small" disabled={currentPage === totalPaymentsPages} onClick={() => setPaymentsPage(currentPage + 1)}>Suivant</Button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="clients-main-container" style={{ padding: "0px 10px 30px 0px", minHeight: "100vh", overflowX: "auto", marginTop: "20px" }}>
      <div>
        <Row gutter={[24, 0]}>
          <Col xs="24" xl={24}>
            {scope && (
              <Title level={4} style={{ marginBottom: 16 }}>
                {scope === "admin"
                  ? "Professionnels sous Gestion de Batilink Team"
                  : "Professionnels sous Gestion Des Partenaires"}
              </Title>
            )}

            {/* ─── Cartes KPI ─────────────────────────────────────────────── */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              {[
                {
                  title: "Total Professionnels",
                  value: kpiStats.total,
                  icon: <UserOutlined style={{ fontSize: 24, color: "#F38181" }} />,
                  active: !scope,
                  onClick: () => history.push("/Utilisateurs"),
                },
                {
                  title: "Sous Gestion de Batilink Team",
                  value: kpiStats.batilink,
                  icon: <TeamOutlined style={{ fontSize: 24, color: "#95E1D3" }} />,
                  active: scope === "admin",
                  onClick: () => history.push("/Utilisateurs?tab=admin"),
                },
                {
                  title: "Sous Gestion Des Partenaires",
                  value: kpiStats.partners,
                  icon: <UserSwitchOutlined style={{ fontSize: 24, color: "#AA96DA" }} />,
                  active: scope === "partner",
                  onClick: () => history.push("/Utilisateurs?tab=partner"),
                },
              ].map((kpi, i) => (
                <Col xs={24} sm={24} md={8} lg={8} xl={8} key={i}>
                  <Card
                    variant={false}
                    onClick={kpi.onClick}
                    style={{
                      cursor: "pointer",
                      borderRadius: 12,
                      border: kpi.active ? "2px solid #fdd119" : "1px solid #f0f0f0",
                      boxShadow: kpi.active ? "0 4px 12px rgba(253,209,25,0.25)" : "0 2px 8px rgba(0,0,0,0.04)",
                      transition: "all 0.2s ease",
                    }}
                    bodyStyle={{ padding: 20 }}
                  >
                    <Row align="middle" justify="space-between">
                      <Col>
                        <div style={{ color: "#8c8c8c", fontSize: 13, marginBottom: 6 }}>{kpi.title}</div>
                        <Title level={3} style={{ margin: 0 }}>{kpi.value}</Title>
                      </Col>
                      <Col>{kpi.icon}</Col>
                    </Row>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Barre de recherche */}
            <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 24 }}>
              <Input
                prefix={<SearchOutlined style={{ color: "#999", marginRight: "8px" }} />}
                type="search"
                placeholder="Rechercher par nom, profession, catégorie…"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                allowClear
                className="unified-search-input"
                style={{ width: 300, height: 45 }}
              />
              <Select
                placeholder="Sélectionner Partenaire"
                value={partnerFilter}
                onChange={value => { setPartnerFilter(value); setPage(1); }}
                style={{ width: 240, height: 45 }}
                suffixIcon={<FilterOutlined />}
                optionFilterProp="label"
                filterOption={(input, option) => String(option?.label || "").toLowerCase().includes(String(input).toLowerCase())}
                options={[
                  { value: "all", label: "Tous les Partenaires" },
                  ...Array.from(new Set((clientss || []).flatMap(c => {
                    const pn = c?.partner_name;
                    if (!pn && pn !== 0) return [];
                    if (Array.isArray(pn)) return pn.map(String).map(s => s.trim()).filter(Boolean);
                    if (typeof pn === "string") return pn.split(/[,;|]+/).map(s => s.trim()).filter(Boolean);
                    return [String(pn).trim()];
                  }).filter(id => id && id.toLowerCase() !== "admin"))).sort().map(id => ({ value: id, label: adminMap[id] || id })),
                ]}
              />
              <Dropdown
                menu={{ items: [{ key: "all", label: "Tous" }, { key: "Activé", label: "Activé" }, { key: "Désactivé", label: "Désactivé" }], onClick: ({ key }) => { setStatusFilter(key); setPage(1); } }}
                trigger={["click"]}
              >
                <Button style={{ height: 45, display: "flex", alignItems: "center", gap: 8, color: "#999" }}>
                  <FilterOutlined />{statusFilter === "all" ? "État du compte" : statusFilter}
                </Button>
              </Dropdown>
              <Dropdown
                menu={{ items: [{ key: "all", label: "Tous" }, ...Array.from(new Set(clientss.map(c => c.governorate).filter(Boolean))).sort().map(gov => ({ key: gov, label: gov }))], onClick: ({ key }) => { setGovernorateFilter(key); setPage(1); } }}
                trigger={["click"]}
              >
                <Button style={{ height: 45, display: "flex", alignItems: "center", gap: 8, color: "#999" }}>
                  <FilterOutlined />{governorateFilter === "all" ? "Gouvernorats" : governorateFilter}
                </Button>
              </Dropdown>
              <Button
                icon={<FilePdfOutlined style={{ color: "#c0392b" }} />}
                onClick={handleExportUsersPdf}
                style={{ height: 45, display: "flex", alignItems: "center", gap: 8, fontWeight: 600, borderColor: "#222", color: "#222" }}
              >
                Export PDF
              </Button>
            </div>

            <div>
              {loading ? <LoadingSpinner /> : (
                <Table
                  columns={columns}
                  dataSource={pagedPartners}
                  pagination={false}
                  rowKey="key"
                  variant={false}
                  size={isMobile ? "small" : "middle"}
                  rowClassName={() => "table-row-light"}
                  scroll={{ x: 800 }}
                  onRow={record => ({
                    style: { cursor: "pointer" },
                    onMouseEnter: e => { e.currentTarget.style.backgroundColor = "#f5f5f5"; },
                    onMouseLeave: e => { e.currentTarget.style.backgroundColor = "transparent"; },
                  })}
                />
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 24, gap: 16 }}>
              <Button type="default" disabled={page === 1} onClick={() => setPage(page - 1)}>Précédent</Button>
              <span style={{ color: "#666", fontSize: 14 }}>Page {page} / {totalPages}</span>
              <Button type="default" disabled={page === totalPages} onClick={() => setPage(page + 1)}>Suivant</Button>
            </div>
          </Col>
        </Row>
      </div>

      {/* ─── Drawer ───────────────────────────────────────────────────────── */}
      <Drawer
        title={(
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", fontFamily: "'Inter', 'Segoe UI', 'Roboto', sans-serif" }}>
            <span style={{ color: "#0f172a", fontWeight: 700 }}>Compte :&nbsp;</span>
            <span style={{ color: selectedUser?.is_activated ? "#15803d" : "#dc2626", fontWeight: 700 }}>
              {selectedUser?.is_activated ? "Activé" : "Désactivé"}
            </span>
          </div>
        )}
        placement={window.innerWidth < 600 ? "top" : "right"}
        height={window.innerWidth < 600 ? "100vh" : undefined}
        width={window.innerWidth < 600 ? undefined : drawerWidth}
        onClose={hideDrawer}
        open={open}
        className={window.innerWidth < 600 ? "user-profile-drawer-top" : "user-profile-drawer"}
        style={window.innerWidth < 600 ? { top: 0, height: "100vh", borderRadius: "12px 12px 0 0" } : {}}
      >
        {loadingUserDetails ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
            <div>Chargement des détails...</div>
          </div>
        ) : userDetails ? (
          <div style={{ background: "#fff", fontFamily: "'Inter', 'Segoe UI', 'Roboto', sans-serif" }}>

            {/* Header profil */}
            <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 12 : 24, padding: isMobile ? "0px 8px 12px 8px" : "24px", borderBottom: "1px solid #f0f0f0", alignItems: isMobile ? "center" : "stretch", textAlign: isMobile ? "center" : "left" }}>
              <div style={{ width: isMobile ? "100%" : 220, flexShrink: 0, textAlign: "center", marginBottom: isMobile ? 8 : 0 }}>
                <Avatar
                  size={isMobile ? 80 : 120}
                  src={userDetails.imageavatar && String(userDetails.imageavatar).trim() !== "" ? userDetails.imageavatar : undefined}
                  style={{ marginTop: 0, border: "4px solid #fff", background: !(userDetails.imageavatar && String(userDetails.imageavatar).trim() !== "") ? avatarColorFor(userDetails.id || userDetails.email || userDetails.First_name) : undefined, color: "#fff", fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                >
                  {!(userDetails.imageavatar && String(userDetails.imageavatar).trim() !== "") ? getInitials(userDetails.First_name, userDetails.Last_name) : null}
                </Avatar>
              </div>
              <div style={{ flex: 1, minWidth: 0, marginTop: isMobile ? 10 : 0 }}>
                <div style={{ marginTop: 12 }}>
                  {(userDetails?.First_name?.trim() || userDetails?.Last_name?.trim()) && (
                    <Title level={4} style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 10, fontSize: isMobile ? 18 : 22, wordBreak: "break-word", textAlign: isMobile ? "center" : "left" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: isMobile ? "center" : "flex-start", gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: "inline-block", wordBreak: "break-word" }}>
                            {`${userDetails.First_name || ""} ${userDetails.Last_name || ""}`.trim()}
                          </span>
                        </div>
                        <VerifiedBadge matricule={userDetails.Matricule} size={isMobile ? 22 : 26} />
                      </div>
                    </Title>
                  )}
                  {(() => {
                    const categories = parseCategories(userDetails.categories);
                    if (categories.length > 0) return <div style={{ marginTop: 12 }}><Text style={{ display: "block", color: "#d8874dff", fontSize: 14, fontWeight: 600 }}>{categories.join(", ")}</Text></div>;
                    return null;
                  })()}
                  <Tag
                    color={userDetails.payment_status === "paid" ? "green" : "red"}
                    style={{ marginTop: 8 }}
                  >
                    {userDetails.payment_status === "paid" ? "Payé" : "Non payé"}
                  </Tag>
                </div>
                {selectedUser?.governorate?.trim() && governorates.includes(selectedUser.governorate) && (
                  <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 6, justifyContent: isMobile ? "center" : "flex-start" }}>
                    <EnvironmentOutlined style={{ color: "#64748b", fontSize: 16 }} />
                    <Text style={{ color: "#64748b", fontSize: 14 }}>{selectedUser.governorate}</Text>
                  </div>
                )}
                {(() => {
                  const avgRating = userDetails.rating || calculateAverageRating(userDetails.reviews);
                  const reviewCount = userDetails.reviews ? (Array.isArray(userDetails.reviews) ? userDetails.reviews.length : (typeof userDetails.reviews === "string" ? JSON.parse(userDetails.reviews).length : 0)) : 0;
                  if (avgRating > 0) return (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, justifyContent: isMobile ? "center" : "flex-start" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                        {[...Array(5)].map((_, i) => <span key={i} style={{ color: i < Math.round(avgRating) ? "#fbbf24" : "#e5e7eb", fontSize: 18 }}>★</span>)}
                      </div>
                      <Text style={{ color: "#64748b", fontSize: 14 }}>{avgRating.toFixed(1)} ({reviewCount} avis)</Text>
                    </div>
                  );
                  return null;
                })()}
              </div>
            </div>

            {/* Tabs */}
            <Tabs
              className="user-profile-tabs"
              defaultActiveKey="info"
              tabBarStyle={{ padding: "0 24px", marginBottom: "10px" }}
              tabBarGutter={80}
              items={[
                {
                  key: "info",
                  label: "Infos personnelles",
                  children: (
                    <div style={{ padding: "24px" }}>
                      {(selectedUser?.email?.trim() || selectedUser?.phone?.trim() || userDetails?.address?.trim()) && (
                        <>
                          <Title level={5} style={{ margin: "0 0 12px 0" }}>Contact</Title>
                          {selectedUser?.email?.trim() && (
                            <div style={{ marginBottom: 16 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                <MailOutlined style={{ color: "#fdd119", fontSize: 17 }} />
                                <Text style={{ fontSize: 15 }}>{selectedUser.email}</Text>
                              </div>
                            </div>
                          )}
                          {selectedUser?.phone?.trim() && (
                            <div style={{ marginBottom: 16 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                <PhoneOutlined style={{ color: "#fdd119", fontSize: 17 }} />
                                <Text style={{ fontSize: 15 }}>{selectedUser.phone}</Text>
                              </div>
                            </div>
                          )}
                          {userDetails?.address?.trim() && (
                            <div style={{ marginBottom: 16 }}>
                              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                                <EnvironmentOutlined style={{ color: "#fdd119", fontSize: 17, marginTop: 2 }} />
                                <Text style={{ fontSize: 15 }}>{userDetails.address}</Text>
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {(userDetails.linkedin?.trim() || userDetails.instagram?.trim() || userDetails.tiktok?.trim() || userDetails.website?.trim()) && (
                        <div style={{ marginTop: 20 }}>
                          <Title level={5} style={{ margin: "0 0 12px 0" }}>Réseaux sociaux</Title>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                            {userDetails.website?.trim() && <a href={userDetails.website.startsWith("http") ? userDetails.website : `https://${userDetails.website}`} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)", color: "#3b82f6", textDecoration: "none", fontSize: 13, fontWeight: 600, border: "1px solid #e2e8f0" }}><GlobalOutlined style={{ fontSize: 14 }} />Site Web</a>}
                            {userDetails.linkedin?.trim() && <a href={userDetails.linkedin.startsWith("http") ? userDetails.linkedin : `https://${userDetails.linkedin}`} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)", color: "#0077b5", textDecoration: "none", fontSize: 13, fontWeight: 600, border: "1px solid #e2e8f0" }}><LinkedinOutlined style={{ fontSize: 14 }} />LinkedIn</a>}
                            {userDetails.instagram?.trim() && <a href={userDetails.instagram.startsWith("http") ? userDetails.instagram : `https://${userDetails.instagram}`} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)", color: "#e4405f", textDecoration: "none", fontSize: 13, fontWeight: 600, border: "1px solid #e2e8f0" }}><InstagramOutlined style={{ fontSize: 14 }} />Instagram</a>}
                            {userDetails.tiktok?.trim() && <a href={userDetails.tiktok.startsWith("http") ? userDetails.tiktok : `https://${userDetails.tiktok}`} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)", color: "#000", textDecoration: "none", fontSize: 13, fontWeight: 600, border: "1px solid #e2e8f0" }}><TikTokOutlined style={{ fontSize: 14 }} />TikTok</a>}
                          </div>
                        </div>
                      )}

                      {(() => {
                        const categories = parseCategories(userDetails.categories);
                        if (categories.length > 0) return (
                          <div style={{ marginTop: 12 }}>
                            <Title level={5} style={{ margin: "0 0 12px 0" }}>Profession</Title>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                              {categories.map((category, idx) => (
                                <div key={idx} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px" }}>
                                  <TagOutlined style={{ color: "#602bd1ff", fontSize: "17px" }} />
                                  <Text style={{ color: "#222", fontSize: 15 }}>{category}</Text>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                        return null;
                      })()}

                      {(() => {
                        const qualifications = parseQualifications(userDetails.qualification);
                        if (qualifications.length > 0) return (
                          <div style={{ marginTop: 20 }}>
                            <Title level={5} style={{ margin: "0 0 12px 0" }}>Qualifications</Title>
                            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                              {qualifications.map((qual, idx) => (
                                <div key={idx} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px" }}>
                                  <SafetyCertificateOutlined style={{ color: "#2d8bd8ff", fontSize: 17 }} />
                                  <Text style={{ color: "#222", fontSize: 15 }}>{qual}</Text>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                        return null;
                      })()}

                      {(() => {
                        const services = parseServicesLocal(userDetails.offered_services);
                        if (services.length > 0) return (
                          <div style={{ marginTop: 20 }}>
                            <Title level={5} style={{ margin: "0 0 12px 0" }}>Services offerts</Title>
                            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                              {services.map((service, idx) => (
                                <div key={idx} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px" }}>
                                  <ToolOutlined style={{ color: "green", fontSize: 17 }} />
                                  <Text style={{ color: "#222", fontSize: 15 }}>{service}</Text>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                        return null;
                      })()}

                      {(() => {
                        const images = parseGalleryImages(userDetails.urlGallery);
                        if (images.length > 0) {
                          const totalGalleryPages = Math.ceil(images.length / galleryPageSize) || 1;
                          const startIdx = (galleryPage - 1) * galleryPageSize;
                          const pagedImages = images.slice(startIdx, startIdx + galleryPageSize);
                          return (
                            <div style={{ marginTop: 20 }}>
                              <Title level={5} style={{ margin: "0 0 12px 0" }}>Galerie</Title>
                              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 12, marginBottom: 16 }}>
                                {pagedImages.map((imageUrl, idx) => <Image key={startIdx + idx} src={imageUrl} alt={`Galerie ${startIdx + idx + 1}`} style={{ width: "100%", height: "120px", objectFit: "cover", borderRadius: 12, border: "2px solid #f1f5f9" }} />)}
                              </div>
                              {totalGalleryPages > 1 && (
                                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, paddingTop: 12, borderTop: "1px solid #f0f0f0" }}>
                                  <Button size="small" disabled={galleryPage === 1} onClick={() => setGalleryPage(galleryPage - 1)} style={{ borderRadius: 6 }}>Précédent</Button>
                                  <span style={{ color: "#666", fontSize: 13, fontWeight: 500 }}>Page {galleryPage} / {totalGalleryPages}</span>
                                  <Button size="small" disabled={galleryPage === totalGalleryPages} onClick={() => setGalleryPage(galleryPage + 1)} style={{ borderRadius: 6 }}>Suivant</Button>
                                </div>
                              )}
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  ),
                },
                {
                  key: "payments",
                  label: "Historique de paiement",
                  children: renderPaymentsHistory(),
                },
              ]}
            />
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
            <div>Aucune donnée disponible</div>
          </div>
        )}
      </Drawer>

      <Modal
        open={notificationModal.open}
        title={`Notification pour ${notificationModal.record?.First_name || "l'utilisateur"}`}
        centered
        onCancel={closeNotificationModal}
        maskClosable={!notificationSending}
        closable={!notificationSending}
        footer={[
          <Button key="cancel" onClick={closeNotificationModal} disabled={notificationSending}>
            Annuler
          </Button>,
          <Button
            key="send"
            type="primary"
            icon={<NotificationOutlined />}
            loading={notificationSending}
            onClick={handleSendNotification}
          >
            Envoyer
          </Button>,
        ]}
      >
        <Tabs
          activeKey={notificationTab}
          onChange={setNotificationTab}
          items={[
            {
              key: "send",
              label: "Envoyer une notification",
              children: (
                <Input.TextArea
                  value={notificationText}
                  onChange={e => setNotificationText(e.target.value)}
                  placeholder="Écrire le message à envoyer..."
                  autoSize={{ minRows: 4, maxRows: 8 }}
                  maxLength={1000}
                  showCount
                  disabled={notificationSending}
                />
              ),
            },
            {
              key: "history",
              label: `Historique d'envoi (${notificationHistory.length})`,
              children: (
                <div style={{ maxHeight: 220, overflowY: "auto" }}>
                  {notificationHistory.length === 0 ? (
                    <div style={{ color: "#999", fontSize: 13 }}>Aucun envoi précédent.</div>
                  ) : (
                    notificationHistory.map((notification, index) => (
                      <div
                        key={`${notification.timestamp || "notification"}-${index}`}
                        style={{ padding: "8px 0", borderBottom: "1px solid #f0f0f0" }}
                      >
                        <div style={{ color: "#666", fontSize: 11, marginBottom: 3 }}>
                          {notification.timestamp
                            ? new Date(notification.timestamp).toLocaleString("fr-FR")
                            : "Date inconnue"}
                        </div>
                        <div style={{ color: "#222", fontSize: 13, whiteSpace: "pre-wrap" }}>
                          {notification.message || "Notification sans message"}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ),
            },
          ]}
        />
      </Modal>

      {/* ─── Modal Confirmation Paiement ───────────────────────────────────── */}
      <Modal
        open={paymentPreview.open}
        title="Confirmer le paiement"
        onCancel={closePaymentPreview}
        maskClosable={true}
        closable={true}
        footer={[
          <Button key="cancel" onClick={closePaymentPreview}>Annuler</Button>,
          <Button
            key="confirm"
            loading={paymentPreview.loading}
            disabled={paymentPreview.loading || paymentPreview.basePrice <= 0}
            style={{ background: "#fdd119", color: "#000", fontWeight: 600, border: "none" }}
            onClick={handleSaveEcheance}
          >
            Confirmer le paiement
          </Button>,
        ]}
        width={480}
      >
        {paymentPreview.loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Calcul du tarif en cours…</div>
        ) : paymentPreview.basePrice <= 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: "#ff4d4f", fontWeight: 600 }}>
            Aucun tarif trouvé pour la profession «&nbsp;{paymentPreview.profession || "—"}&nbsp;». Vérifiez la table category.
          </div>
        ) : (
          <div style={{ padding: "8px 4px" }}>
            {/* Bloc utilisateur */}
            {userDetails && (
              <div style={{ padding: 12, background: "#fafafa", borderRadius: 8, marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
                <Avatar
                  src={userDetails.imageavatar || undefined}
                  size={40}
                  style={{ background: !userDetails.imageavatar ? avatarColorFor(userDetails.id) : undefined, color: "#fff", fontWeight: 700 }}
                >
                  {!userDetails.imageavatar ? getInitials(userDetails.First_name, userDetails.Last_name) : null}
                </Avatar>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span style={{ fontWeight: 700, color: "#1a1a1a" }}>{`${userDetails.First_name || ""} ${userDetails.Last_name || ""}`.trim() || ""}</span>
                </div>
              </div>
            )}

            {/* Détail du tarif */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "0 4px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ color: "#555" }}>Prix annuel de base</span>
                <span style={{ fontWeight: 600, color: paymentPreview.discount > 0 ? "#999" : "#1a1a1a", textDecoration: paymentPreview.discount > 0 ? "line-through" : "none" }}>
                  {paymentPreview.basePrice.toLocaleString("fr-FR")} DT
                </span>
              </div>

              {paymentPreview.discount > 0 && (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ color: "#c41d7f" }}>
                      Code promo appliqué
                    </span>
                    <Tag color="magenta" style={{ fontWeight: 700, margin: 0 }}>−{paymentPreview.discount}%</Tag>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ color: "#555" }}>Prix annuel après promo</span>
                    <span style={{ fontWeight: 700, color: "#c41d7f" }}>
                      {paymentPreview.finalPrice.toLocaleString("fr-FR")} DT
                    </span>
                  </div>
                </>
              )}

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ color: "#555" }}>Nombre d'années</span>
                <span style={{ fontWeight: 600, color: "#1a1a1a" }}>{paymentPreview.years}</span>
              </div>

              <div style={{ borderTop: "1px dashed #e0e0e0", margin: "8px 0" }}></div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontWeight: 700, color: "#1a1a1a", fontSize: 16 }}>Total à payer</span>
                <span style={{ fontWeight: 800, color: "#52c41a", fontSize: 22 }}>
                  {paymentPreview.total.toLocaleString("fr-FR")} DT
                </span>
              </div>

              {paymentPreview.discount > 0 && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: -4 }}>
                  <span style={{ fontSize: 12, color: "#888" }}>
                    Économie&nbsp;:&nbsp;
                    <strong style={{ color: "#c41d7f" }}>
                      {(paymentPreview.totalOriginal - paymentPreview.total).toLocaleString("fr-FR")} DT
                    </strong>
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* ─── Modal Promouvoir ─────────────────────────────────────────────── */}
      <Modal
        open={promoteModalOpen}
        title="Promouvoir en partenaire"
        onCancel={closePromoteModal}
        maskClosable={!promotingId}
        closable={!promotingId}
        footer={[
          <Button key="cancel" onClick={closePromoteModal} disabled={!!promotingId}>Annuler</Button>,
          <Button key="submit" loading={!!promotingId} disabled={promoteCodeCheck.status === "taken" || promoteCodeCheck.status === "checking" || promoteCodeCheck.status === "invalid" || !promoteCode} style={{ background: "#fdd119", color: "#000", fontWeight: 600, border: "none" }} onClick={() => selectedProForPromotion && promoteToPartner(selectedProForPromotion)}>Promouvoir</Button>,
        ]}
        centered
        width={500}
      >
        {selectedProForPromotion && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Avatar src={selectedProForPromotion.imageavatar || undefined} size={48} style={{ background: !selectedProForPromotion.imageavatar ? avatarColorFor(selectedProForPromotion.id) : undefined, color: "#fff", fontWeight: 700 }}>
                {!selectedProForPromotion.imageavatar ? getInitials(selectedProForPromotion.First_name, selectedProForPromotion.Last_name) : null}
              </Avatar>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontWeight: 600, color: "#1a1a1a" }}>{`${selectedProForPromotion.First_name || ""} ${selectedProForPromotion.Last_name || ""}`.trim() || "---"}</span>
                <span style={{ color: "#666", fontSize: 12 }}>{selectedProForPromotion.email}</span>
                {selectedProForPromotion.governorate && <span style={{ color: "#666", fontSize: 12 }}>{selectedProForPromotion.governorate}</span>}
              </div>
            </div>
            <div style={{ padding: 10, background: "#fffbe6", border: "1px solid #ffe58f", borderRadius: 6, fontSize: 13 }}>
              <strong>{`${selectedProForPromotion.First_name || ""} ${selectedProForPromotion.Last_name || ""}`.trim()}</strong>{" "}
              deviendra partenaire et pourra profiter du programme de parrainage avec le code saisi ci-dessous.
              <em>{" "}Son compte sera activé automatiquement et une notification sera envoyée sur l'application mobile.</em>
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 500, fontSize: 13 }}>Code de parrainage (3 chiffres)</label>
              <Input
                placeholder="Ex: 123"
                maxLength={3}
                inputMode="numeric"
                value={promoteCode}
                onChange={e => setPromoteCode((e.target.value || "").replace(/\D/g, "").slice(0, 3))}
                status={promoteCodeCheck.status === "taken" || promoteCodeCheck.status === "invalid" ? "error" : undefined}
              />
              <div style={{ marginTop: 6, minHeight: 18, fontSize: 12 }}>
                {promoteCodeCheck.status === "checking" && <span style={{ color: "#999" }}>Vérification…</span>}
                {promoteCodeCheck.status === "available" && <span style={{ color: "#52c41a", fontWeight: 600 }}>✓ Code disponible</span>}
                {promoteCodeCheck.status === "taken" && (
                  <div>
                    <span style={{ color: "#ff4d4f", fontWeight: 600 }}>✗ Ce code est déjà utilisé</span>
                    {promoteCodeSuggestions.loading && (
                      <div style={{ color: "#999", marginTop: 4 }}>Recherche de codes disponibles…</div>
                    )}
                    {!promoteCodeSuggestions.loading && promoteCodeSuggestions.items.length > 0 && (
                      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                        <span style={{ color: "#666" }}>Suggestions :</span>
                        {promoteCodeSuggestions.items.map(s => (
                          <Tag
                            key={s}
                            color="gold"
                            style={{ cursor: "pointer", margin: 0, fontWeight: 600 }}
                            onClick={() => setPromoteCode(s)}
                          >
                            {s}
                          </Tag>
                        ))}
                      </div>
                    )}
                    {!promoteCodeSuggestions.loading && promoteCodeSuggestions.items.length === 0 && (
                      <div style={{ color: "#999", marginTop: 4 }}>Aucune suggestion disponible</div>
                    )}
                  </div>
                )}
                {promoteCodeCheck.status === "invalid" && <span style={{ color: "#ff4d4f" }}>{promoteCodeCheck.message}</span>}
                {promoteCodeCheck.status === "error" && <span style={{ color: "#ff4d4f" }}>Erreur de vérification</span>}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ─── Modal échéance (legacy) ──────────────────────────────────────── */}
      <Modal
        open={echeanceModal.open}
        title="Ajouter une échéance"
        onCancel={() => setEcheanceModal({ open: false, record: null })}
        onOk={() => { setEcheanceModal({ open: false, record: null }); message.success("Échéance ajoutée"); }}
        okText="Ajouter"
        cancelText="Annuler"
        className="custom-modal-message"
      >
        <p>Client: {echeanceModal.record ? `${echeanceModal.record.First_name || ""} ${echeanceModal.record.Last_name || ""}` : "-"}</p>
      </Modal>
    </div>
  );
}

export default PartnerClients;
